
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useOrganizations, useUpdateOrganization } from '@/hooks/useOrganizations';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { OrganizationCard } from '@/components/organizations/organization-card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const orgSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  description: z.string().optional(),
});

type OrgFormData = z.infer<typeof orgSchema>;

function OrganizationsPage() {
  usePageTitle('Organizations');
  const { organizations: contextOrgs, currentOrg, switchOrganization, loadOrganizations, pendingOnboarding, setPendingOnboarding } = useAuth();
  const { createOrganization, isCreating } = useOrganizations();
  const updateOrg = useUpdateOrganization();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const showOnboarding = pendingOnboarding;
  const setShowOnboarding = setPendingOnboarding;
  const [error, setError] = useState('');
  const [switchError, setSwitchError] = useState('');
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OrgFormData>({
    resolver: zodResolver(orgSchema),
  });

  const onSubmit = async (data: OrgFormData) => {
    try {
      setError('');
      const org = await createOrganization(data);
      await loadOrganizations();
      await switchOrganization(org.id);
      reset();
      setShowCreateDialog(false);
      setShowOnboarding(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    }
  };

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    await loadOrganizations();
  };

  const handleSwitch = async (orgId: string) => {
    if (switchingOrgId) return;
    setSwitchingOrgId(orgId);
    try {
      setSwitchError('');
      await switchOrganization(orgId);
    } catch (err) {
      setSwitchError(err instanceof Error ? err.message : 'Failed to switch organization');
    } finally {
      setSwitchingOrgId(null);
    }
  };

  const handleUpdateOrgName = async (orgId: string, name: string, description?: string) => {
    try {
      await updateOrg.mutateAsync({ orgId, data: { name, description } });
      await loadOrganizations();
    } catch (err) {
      setSwitchError(err instanceof Error ? err.message : 'Failed to update organization');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organizations"
        askAi
        description="Manage the organizations you belong to"
        hideTitleOnMobile={false}
        actions={
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            if (!open) { reset(); setError(''); }
            setShowCreateDialog(open);
          }}>
            <DialogTrigger asChild>
              <Button>Create New Organization</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Organization</DialogTitle>
              <DialogDescription>
                Add a new organization to manage your data separately.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {error && (
                <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Acme Corp"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  placeholder="What's this organization for?"
                  rows={3}
                  {...register('description')}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    reset();
                    setShowCreateDialog(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        }
      />

      {switchError && (
        <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          {switchError}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {contextOrgs.map((org) => (
          <OrganizationCard
            key={org.id}
            org={org}
            isCurrent={org.id === currentOrg?.id}
            isSwitching={switchingOrgId === org.id}
            onSwitch={handleSwitch}
            onSave={handleUpdateOrgName}
          />
        ))}
      </div>

      {contextOrgs.length === 0 && (
        <EmptyState
          icon={Building2}
          title="No organizations"
          description="Create your first organization to get started."
          action={{
            label: "Create Organization",
            onClick: () => setShowCreateDialog(true),
          }}
        />
      )}

      <OnboardingModal
        open={showOnboarding}
        onComplete={handleOnboardingComplete}
        initialStep="select-industry"
      />
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/organizations')({ component: OrganizationsPage });
